import { useCallback, useMemo } from 'react';

import type { LDClient as LDClientNode } from '@launchdarkly/node-server-sdk';
import config from 'config';
import type { LDClient as LDClientReact } from 'launchdarkly-react-client-sdk';
import { useSelector } from 'react-redux';
import type { Store } from 'redux';

import { getBuilderMembershipStatusSelector } from '../../builder/utils/getBuilderMembershipStatus';
import {
  getCustomer,
  getCustomerDetail,
  getMembership,
} from '../../techstyle-shared/react-accounts';
import { useAdminTools } from '../../techstyle-shared/react-admin';
import { useBreakpoint } from '../../techstyle-shared/react-components';
import { getDirectMarketingDisposition } from '../../techstyle-shared/react-marketing';
import {
  createSelector,
  getSession,
  getSessionVisitorId,
} from '../../techstyle-shared/redux-core';

import { getBrandValue } from './brandNameHelper';
import { getGender } from './useGender';

export type LDContextValue = {
  kind: 'multi';
  locale: {
    kind: 'locale';
    key: string;
    domain: string;
    builderLocale: string;
  };
  user:
    | {
        kind: 'user';
        key: string;
        email: string;
        experienceId: string;
        gender: string;
        isAdmin: boolean;
        isFirstTimeVisitor: boolean;
        isLead: boolean;
        isVip: boolean;
        isDowngraded: boolean;
        isPaygo: boolean;
        loggedIn: true;
        membershipStatus: string;
        membershipTypeId: number;
        sessionId: string;
        signupSource: string;
        statusLabel: string;
      }
    | {
        kind: 'user';
        key: string;
        anonymous: true;
        experienceId: string;
        gender: string;
        isAdmin: boolean;
        isFirstTimeVisitor: boolean;
        isLead: boolean;
        isVip: boolean;
        isDowngraded: boolean;
        isPaygo: boolean;
        loggedIn: false;
        membershipStatus: string;
        sessionId: string;
      };
  guest: {
    kind: 'guest';
    key: string;
    sessionId: string;
    isFirstTimeVisitor: boolean;
    membershipStatus: string;
    experienceId: string;
    geoCountry: string;
  };
  session: {
    kind: 'session';
    key: string;
    membershipStatus: string;
    geoCountry: string;
    sessionVisitor: number;
  };
  device: {
    kind: 'device';
    key: 'Tablet' | 'Mobile' | 'Desktop';
    platform: 'web';
  };
};

export const getLaunchDarklyContext = createSelector(
  [
    (state) => state.domain,
    getSession,
    getCustomer,
    getMembership,
    getSessionVisitorId,
    getBuilderMembershipStatusSelector,
    (state) => state.intl.geoCountry,
    // @ts-ignore -- Inferred type for `getCustomerDetail` is incorrect
    (state) => getCustomerDetail(state, 'signup_source')?.value,
    (state) => state.cart.isVipCart,
    getDirectMarketingDisposition,
    getGender,
  ],
  (
    domain,
    session,
    customer,
    membership,
    sessionVisitorId,
    membershipStatus,
    geoCountry,
    signupSource,
    isVipCart,
    disposition,
    { gender }
  ) => {
    const { tld, parentCookieDomain } = domain;
    const { isLoggedIn, isAdmin, sessionId, isFirstTimeVisitor } = session;
    const { email, id: customerId } = customer;
    const {
      isVip,
      isDowngraded,
      isLead,
      statusLabel,
      membershipTypeId,
      isPaygo,
    } = membership;
    const { experience: experienceId = '' } = disposition;
    const builderLocale = config.get('public.intl.builderLocales')[tld];

    const brand = getBrandValue({
      default: 'fabletics',
      yitty: 'yitty',
      savage: 'savagex',
    });

    const localeContext = {
      kind: 'locale',
      domain: tld,
      key: `${brand}${tld}`,
      fullDomain: parentCookieDomain,
      builderLocale,
    };

    const userContext = {
      kind: 'user',
      sessionId: sessionId,
      sessionVisitor: sessionVisitorId,
      isFirstTimeVisitor: isFirstTimeVisitor,
      isAdmin: isAdmin,
      membershipStatus: membershipStatus,
      isActivating: !isVip && isVipCart,
      isLead: isLead,
      isVip: isVip,
      isPaygo: isPaygo,
      isDowngraded: isDowngraded,
      experienceId,
      locale: builderLocale.split('-')[1], // e.g. 'US' from 'en-US'
      gender,
      brand,
      ...(isLoggedIn
        ? {
            key: `${customerId}`,
            email: email,
            loggedIn: true,
            membershipTypeId: membershipTypeId,
            statusLabel: statusLabel,
            signupSource,
          }
        : {
            anonymous: true,
            key: `${sessionVisitorId}`,
            isActivating: false,
            loggedIn: false,
          }),
    };

    const guestContext = {
      kind: 'guest',
      key: `${sessionVisitorId}`,
      sessionId: sessionId,
      isFirstTimeVisitor: isFirstTimeVisitor,
      membershipStatus: membershipStatus,
      experienceId,
      geoCountry,
    };

    const deviceContext = {
      kind: 'device',
      key: 'Mobile',
      platform: 'web',
    };

    const sessionContext = {
      kind: 'session',
      key: `${sessionId}`,
      membershipStatus: membershipStatus,
      geoCountry,
      sessionVisitor: sessionVisitorId,
    };

    return {
      kind: 'multi',
      locale: localeContext,
      user: userContext,
      guest: guestContext,
      device: deviceContext,
      session: sessionContext,
    } as LDContextValue;
  }
);

export const useLaunchDarklyContextValue = () => {
  const { enabled } = useAdminTools();
  const { isMobile } = useBreakpoint();
  const { matches: isTablet } = useBreakpoint(
    `(min-width: 768px) and (max-width: 1023px)`
  );
  const getDeviceContext = useCallback(() => {
    if (isTablet) {
      return 'Tablet';
    } else if (isMobile) {
      return 'Mobile';
    } else {
      return 'Desktop';
    }
  }, [isTablet, isMobile]);
  const launchDarklyContextValues = useSelector(getLaunchDarklyContext);
  const fullContextValues = useMemo(
    () => ({
      ...launchDarklyContextValues,
      user: {
        ...launchDarklyContextValues.user,
        isAdmin: launchDarklyContextValues?.user?.isAdmin || enabled,
      },
      device: {
        ...launchDarklyContextValues.device,
        key: getDeviceContext(),
      },
    }),
    [enabled, getDeviceContext, launchDarklyContextValues]
  );
  return fullContextValues;
};

/**
 * A helper function to retrieve LD flags inside of `getInitialProps`.
 * This is needed because the LD client works differently on the server vs the client.
 */
export const getLDFlagWithCtx = async ({
  flag,
  ctx,
}: {
  /** The LD Flag to retrieve */
  flag: string;
  /** The context to use when retrieving the flag, this is the parameter for `getInitialProps`  */
  ctx: { ldClient?: LDClientNode | LDClientReact; store: Store };
}) => {
  if (!ctx.ldClient) {
    return null;
  }

  if (typeof window !== 'undefined') {
    // @ts-ignore -- We know this will be the React client at this point
    const ldClient: LDClientReact = ctx.ldClient;
    return await ldClient.variation(flag);
  }

  // @ts-ignore -- We know this will be the Node client at this point
  const ldClient: LDClientNode = ctx.ldClient;
  const fullLDContext = getLaunchDarklyContext(ctx.store.getState() as never);

  return await ldClient.variation(flag, fullLDContext, null);
};

export const GlobalLDSymbol = Symbol('GlobalLD');
